import React, { useState, useEffect, useRef } from 'react';

import {
  Stack,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormHelperText,
} from '@mui/material';
import { useSelector } from 'react-redux';

export default function CheckpointSelect({
  value,
  onChange,
  error = false,
  onError,
  disabled = false,
  required = false,
  field_id = 'checkpoint',
}) {
  const [is_public, setIsPublic] = useState(value ? value.public : false);
  const [id, setId] = useState(value ? value.id : '');

  const checkpoints = useSelector((state) => state.checkpoints);
  const publicCheckpoints = useSelector((state) => state.publicCheckpoints);

  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    setId(value?.id || '');
    setIsPublic(Boolean(value?.public));
  }, [value]);

  useEffect(() => {
    if (!id) {
      if (required) {
        onErrorRef.current?.('Checkpoint cannot be blank');
      }
      return;
    }

    const active_list = is_public ? publicCheckpoints : checkpoints;
    if (active_list === null) {
      return;
    }

    const exists = (active_list || []).some(
      (cp) => cp.checkpoint_uuid === id,
    );

    if (!exists) {
      onErrorRef.current?.('Checkpoint missing or deleted');
      return;
    }

    if (
      error === 'Checkpoint missing or deleted' ||
      error === 'Checkpoint cannot be blank'
    ) {
      onErrorRef.current?.(false);
    }
  }, [id, is_public, checkpoints, publicCheckpoints, required, error]);

  const emitChange = (next_public, next_id, next_as) => {
    onChangeRef.current?.({
      public: next_public,
      id: next_id,
      as: next_as,
      name: next_as,
    });
  };

  const handleChange = (event, val) => {
    const { value: new_id, name: new_as } = val.props;
    setId(new_id);
    emitChange(is_public, new_id, new_as);
    if (new_id === '') {
      onErrorRef.current?.('Checkpoint cannot be blank');
    } else {
      onErrorRef.current?.(false);
    }
  };

  const handlePublicChange = () => {
    const next_public = !is_public;
    setIsPublic(next_public);
    setId('');
    emitChange(next_public, '', '');
    onErrorRef.current?.('Checkpoint cannot be blank');
  };

  const checkpoint_options = is_public
    ? publicCheckpoints || []
    : checkpoints || [];

  return (
    <Stack
      direction='row'
      justifyContent='flex-start'
      alignItems='center'
      spacing={1}
    >
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={is_public}
              onChange={handlePublicChange}
              disabled={disabled}
            />
          }
          label='Public'
        />
      </Box>
      <Box sx={{ minWidth: '300px', flex: 1 }}>
        <FormControl
          fullWidth
          required={required}
          error={Boolean(error)}
          disabled={disabled}
        >
          <InputLabel id={`${field_id}-label`}>Checkpoint</InputLabel>
          <Select
            labelId={`${field_id}-label`}
            id={`${field_id}-id`}
            value={id}
            label='Checkpoint'
            onChange={handleChange}
            aria-describedby={`${field_id}-helper-text`}
          >
            {checkpoint_options.map((checkpoint) => {
              return (
                <MenuItem
                  key={checkpoint.checkpoint_uuid}
                  value={checkpoint.checkpoint_uuid}
                  name={checkpoint.name}
                >
                  {checkpoint.name}
                </MenuItem>
              );
            })}
          </Select>
          {error ? (
            <FormHelperText id={`${field_id}-helper-text`}>
              {error}
            </FormHelperText>
          ) : undefined}
        </FormControl>
      </Box>
    </Stack>
  );
}
